import { roomTaxesFromDiscounted } from './roomPricing';
import { ROOM_DAY_KEYS } from './roomDayPricing';

export function guestPricingEnabled(price) {
  const gp = price && price.guestPricing;
  return Boolean(gp && gp.enabled && Array.isArray(gp.tiers) && gp.tiers.length > 0);
}

function dayHasGuestTiers(daySlice) {
  return Boolean(daySlice && Array.isArray(daySlice.guestTiers) && daySlice.guestTiers.length > 0);
}

function sortedTiers(tiers) {
  return [...(tiers || [])]
    .map((t) => ({
      adults: Math.max(1, Math.floor(Number(t.adults) || 1)),
      mrp: Number(t.mrp) || 0,
      discountedPrice: Number(t.discountedPrice) || 0
    }))
    .sort((a, b) => a.adults - b.adults);
}

function normalizeGuestBlock(block, roomCapacity = 10) {
  if (!block || typeof block !== 'object') return null;
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));
  const tiers = sortedTiers(block.tiers || block.guestTiers).filter((t) => t.adults <= cap);
  if (!tiers.length) return null;

  const maxTier = tiers[tiers.length - 1].adults;
  const threshold = Math.min(
    cap,
    Math.max(1, Math.floor(Number(block.thresholdAdults)) || maxTier)
  );

  return {
    tiers,
    thresholdAdults: threshold,
    extraAdultAddon: {
      mrp: Number(block.extraAdultAddon?.mrp) || 0,
      discountedPrice: Number(block.extraAdultAddon?.discountedPrice) || 0
    }
  };
}

/** Guest tier config for one weekday (day-specific table or room default). */
export function getGuestConfigForWeekday(price, dayKey, roomCapacity = 10) {
  if (!guestPricingEnabled(price)) return null;
  const gp = price.guestPricing;
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));

  const useDay = Boolean(price.useDayOfWeekPricing);
  const daySlice =
    useDay && price.dayPricing && typeof price.dayPricing === 'object'
      ? price.dayPricing[dayKey]
      : null;

  if (daySlice && dayHasGuestTiers(daySlice)) {
    const fromDay = normalizeGuestBlock(
      {
        tiers: daySlice.guestTiers,
        thresholdAdults: daySlice.thresholdAdults,
        extraAdultAddon: daySlice.extraAdultAddon
      },
      cap
    );
    if (fromDay) return fromDay;
  }

  return normalizeGuestBlock(gp, cap);
}

export function resolveGuestOccupancySlice(guestConfig, adults) {
  const n = Math.max(1, Math.floor(Number(adults) || 1));
  const tiers = sortedTiers(guestConfig?.tiers);
  if (!tiers.length) {
    return { mrp: 0, discountedPrice: 0, taxes: 0 };
  }

  const maxTierAdults = tiers[tiers.length - 1].adults;
  const threshold = Math.max(
    1,
    Math.floor(Number(guestConfig.thresholdAdults)) || maxTierAdults
  );
  const addon = guestConfig.extraAdultAddon;

  let tier =
    tiers.find((t) => t.adults === n) ||
    tiers.filter((t) => t.adults <= n).pop() ||
    tiers[0];

  let mrp = tier.mrp;
  let discountedPrice = tier.discountedPrice;

  if (n > threshold) {
    const threshTier = tiers.find((t) => t.adults === threshold) || tiers[tiers.length - 1];
    const extra = n - threshold;
    mrp = threshTier.mrp + extra * (Number(addon?.mrp) || 0);
    discountedPrice = threshTier.discountedPrice + extra * (Number(addon?.discountedPrice) || 0);
  }

  const taxes = roomTaxesFromDiscounted(discountedPrice);
  return { mrp, discountedPrice, taxes };
}

/** Per-night price for a date + adults (weekday guest tiers when set). */
export function resolveNightPriceForOccupancy(price, dateYmd, adults, roomCapacity = 10) {
  const p = price && typeof price === 'object' ? price : {};
  const currency = p.currency || 'INR';
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));
  const dt = parseYmdUtc(dateYmd);
  const dayKey = ROOM_DAY_KEYS[dt ? dt.getUTCDay() : 0] || 'sun';

  if (adults != null && guestPricingEnabled(p)) {
    const cfg = getGuestConfigForWeekday(p, dayKey, cap);
    if (cfg) {
      const g = resolveGuestOccupancySlice(cfg, adults);
      return { ...g, currency };
    }
  }

  const useDay = Boolean(p.useDayOfWeekPricing);
  const daySlice =
    useDay && p.dayPricing && typeof p.dayPricing === 'object' ? p.dayPricing[dayKey] : null;
  if (daySlice && daySlice.discountedPrice != null) {
    const dp = Number(daySlice.discountedPrice) || 0;
    return {
      mrp: daySlice.mrp != null ? Number(daySlice.mrp) : Number(p.mrp) || 0,
      discountedPrice: dp,
      taxes: roomTaxesFromDiscounted(dp),
      currency
    };
  }
  const dp = Number(p.discountedPrice) || 0;
  return {
    mrp: Number(p.mrp) || 0,
    discountedPrice: dp,
    taxes: roomTaxesFromDiscounted(dp),
    currency
  };
}

function parseYmdUtc(ymd) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

/** @deprecated use resolveNightPriceForOccupancy */
export function applyGuestPricingToNightSlice(nightSlice, price, adults, roomCapacity = 10) {
  const dt = nightSlice?._dateYmd;
  if (dt) {
    return resolveNightPriceForOccupancy(price, dt, adults, roomCapacity);
  }
  if (!guestPricingEnabled(price)) return nightSlice;
  const guestSlice = resolveGuestOccupancySlice(price.guestPricing, adults);
  return {
    ...nightSlice,
    mrp: guestSlice.mrp,
    discountedPrice: guestSlice.discountedPrice,
    taxes: guestSlice.taxes,
    currency: nightSlice.currency || price.currency || 'INR'
  };
}

export function minDiscountedForGuestPricing(price, roomCapacity = 10) {
  if (!guestPricingEnabled(price)) return null;
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));
  let min = Infinity;

  const consider = (cfg) => {
    if (!cfg || !cfg.tiers.length) return;
    for (const t of cfg.tiers) {
      min = Math.min(min, t.discountedPrice);
    }
    const addonDp = Number(cfg.extraAdultAddon?.discountedPrice) || 0;
    if (addonDp > 0 && min !== Infinity) {
      min = Math.min(min, min + addonDp);
    }
  };

  if (price.useDayOfWeekPricing && price.dayPricing) {
    for (const key of ROOM_DAY_KEYS) {
      consider(getGuestConfigForWeekday(price, key, cap));
    }
  } else {
    consider(getGuestConfigForWeekday(price, 'sun', cap));
  }

  return min === Infinity ? 0 : min;
}

export function allocateAdultsPerRoomLine(totalAdults, roomLines) {
  const adults = Math.max(1, Math.floor(Number(totalAdults) || 1));
  const units = [];
  for (const line of roomLines || []) {
    const qty = Math.max(1, Math.floor(Number(line.quantity) || 1));
    const cap = Math.max(1, Math.floor(Number(line.capacity) || 10));
    for (let i = 0; i < qty; i += 1) {
      units.push({ roomId: line.roomId, capacity: cap });
    }
  }
  if (!units.length) {
    return [{ roomId: roomLines?.[0]?.roomId, adults }];
  }

  let remaining = adults;
  const perUnit = Math.max(1, Math.ceil(adults / units.length));
  const byRoom = new Map();

  for (const u of units) {
    const assign = Math.min(u.capacity, perUnit, remaining);
    const n = Math.max(1, assign);
    remaining -= n;
    const prev = byRoom.get(u.roomId) || 0;
    byRoom.set(u.roomId, Math.max(prev, n));
  }

  if (remaining > 0) {
    const firstId = units[0].roomId;
    byRoom.set(firstId, (byRoom.get(firstId) || 1) + remaining);
  }

  return (roomLines || []).map((line) => ({
    roomId: line.roomId,
    adults: byRoom.get(line.roomId) || Math.min(adults, line.capacity || 10)
  }));
}

export function adultsForRoomDisplayQuote(totalAdults, roomCapacity, totalBookedUnits, roomId, allSelectedRooms) {
  const adults = Math.max(1, Math.floor(Number(totalAdults) || 1));
  const lines = (allSelectedRooms || []).map((r) => ({
    roomId: r.id || r.roomId,
    quantity: r.quantity || 1,
    capacity: r.capacity || r.sleeps || 10
  }));
  if (!lines.length) {
    return Math.min(adults, Math.max(1, Number(roomCapacity) || 10));
  }
  const alloc = allocateAdultsPerRoomLine(adults, lines);
  const hit = alloc.find((a) => a.roomId === roomId);
  return hit?.adults || Math.min(adults, Math.max(1, Number(roomCapacity) || 10));
}

export function defaultGuestTiersFromBase({ mrp, discountedPrice, capacity }) {
  const cap = Math.max(1, Math.min(10, Math.floor(Number(capacity) || 2)));
  const tiers = [];
  for (let a = 1; a <= cap; a += 1) {
    const dp = Math.round((Number(discountedPrice) || 0) * (1 + (a - 1) * 0.15));
    const m = Math.round((Number(mrp) || dp) * (1 + (a - 1) * 0.15));
    tiers.push({
      adults: a,
      mrp: m,
      discountedPrice: dp,
      taxes: roomTaxesFromDiscounted(dp)
    });
  }
  return tiers;
}

export function normalizeGuestPricingForSave(price, roomCapacity = 10) {
  const p = { ...(price || {}) };
  const gp = p.guestPricing && typeof p.guestPricing === 'object' ? { ...p.guestPricing } : {};
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));

  if (!gp.enabled) {
    p.guestPricing = {
      enabled: false,
      tiers: [],
      thresholdAdults: cap,
      extraAdultAddon: { mrp: 0, discountedPrice: 0, taxes: 0 }
    };
    return p;
  }

  const tiers = sortedTiers(gp.tiers)
    .filter((t) => t.adults <= cap)
    .map((t) => ({
      adults: t.adults,
      mrp: t.mrp,
      discountedPrice: t.discountedPrice,
      taxes: roomTaxesFromDiscounted(t.discountedPrice)
    }));

  const maxTier = tiers.length ? tiers[tiers.length - 1].adults : cap;
  const threshold = Math.min(
    cap,
    Math.max(1, Math.floor(Number(gp.thresholdAdults)) || maxTier)
  );

  p.guestPricing = {
    enabled: true,
    tiers,
    thresholdAdults: threshold,
    extraAdultAddon: {
      mrp: Number(gp.extraAdultAddon?.mrp) || 0,
      discountedPrice: Number(gp.extraAdultAddon?.discountedPrice) || 0,
      taxes: roomTaxesFromDiscounted(Number(gp.extraAdultAddon?.discountedPrice) || 0)
    }
  };

  if (p.useDayOfWeekPricing && p.dayPricing && typeof p.dayPricing === 'object') {
    const nextDay = { ...p.dayPricing };
    for (const key of ROOM_DAY_KEYS) {
      const slice = nextDay[key];
      if (!slice || typeof slice !== 'object' || !dayHasGuestTiers(slice)) continue;
      const dayTiers = sortedTiers(slice.guestTiers)
        .filter((t) => t.adults <= cap)
        .map((t) => ({
          adults: t.adults,
          mrp: t.mrp,
          discountedPrice: t.discountedPrice,
          taxes: roomTaxesFromDiscounted(t.discountedPrice)
        }));
      const dayMax = dayTiers.length ? dayTiers[dayTiers.length - 1].adults : cap;
      const dayThresh = Math.min(
        cap,
        Math.max(1, Math.floor(Number(slice.thresholdAdults)) || dayMax)
      );
      const dayAddonDp = Number(slice.extraAdultAddon?.discountedPrice) || 0;
      nextDay[key] = {
        ...slice,
        guestTiers: dayTiers,
        thresholdAdults: dayThresh,
        extraAdultAddon: {
          mrp: Number(slice.extraAdultAddon?.mrp) || 0,
          discountedPrice: dayAddonDp,
          taxes: roomTaxesFromDiscounted(dayAddonDp)
        }
      };
    }
    p.dayPricing = nextDay;
  }

  return p;
}

export function getTotalBookingGuests(guestData) {
  const adults = Math.max(0, Math.floor(Number(guestData?.adults) || 0));
  const children = Math.max(0, Math.floor(Number(guestData?.children) || 0));
  return Math.max(1, adults + children);
}

export function getRoomNumericCapacity(room) {
  const cap = room?.capacity ?? room?.sleeps;
  if (typeof cap === 'number' && !Number.isNaN(cap)) {
    return Math.max(0, Math.floor(cap));
  }
  const parsed = parseInt(String(cap ?? ''), 10);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export function getBookedRoomUnits(roomQuantities) {
  return Object.values(roomQuantities || {}).reduce(
    (sum, qty) => sum + Math.max(0, Math.floor(Number(qty) || 0)),
    0
  );
}

export function buildBookedRoomLines(selectedRooms, roomQuantities) {
  return (selectedRooms || []).map((room) => ({
    roomId: room.id || room.roomId,
    quantity: Math.max(1, Math.floor(Number(roomQuantities?.[room.id]) || Number(room.quantity) || 1)),
    capacity: Math.max(1, getRoomNumericCapacity(room) || 10)
  }));
}

export function canAccommodateGuestsWithUnits(totalGuests, unitCapacities) {
  if (!unitCapacities.length) return false;
  const caps = [...unitCapacities].sort((a, b) => b - a);
  let remaining = Math.max(1, Math.floor(Number(totalGuests) || 1));
  for (const cap of caps) {
    remaining -= Math.min(cap, remaining);
    if (remaining <= 0) return true;
  }
  return false;
}

/** Minimum sleeps required for the next room unit given current selection and guest filters. */
export function getMinCapacityRequiredForNextUnit(guestData, roomQuantities, selectedRooms) {
  const totalGuests = getTotalBookingGuests(guestData);
  const requestedRooms = Math.max(1, Math.floor(Number(guestData?.rooms) || 1));
  const bookedUnits = getBookedRoomUnits(roomQuantities);
  const remainingSlots = requestedRooms - bookedUnits;

  if (remainingSlots <= 0) {
    return Infinity;
  }

  const units = [];
  for (const line of buildBookedRoomLines(selectedRooms, roomQuantities)) {
    for (let i = 0; i < line.quantity; i += 1) {
      units.push(line.capacity);
    }
  }

  let remainingGuests = totalGuests;
  const sortedUnits = [...units].sort((a, b) => b - a);
  for (const cap of sortedUnits) {
    remainingGuests -= Math.min(cap, remainingGuests);
  }

  return Math.max(1, Math.ceil(remainingGuests / remainingSlots));
}

export function canAddRoomUnit(room, guestData, roomQuantities, selectedRooms) {
  const required = getMinCapacityRequiredForNextUnit(guestData, roomQuantities, selectedRooms);
  if (!Number.isFinite(required) || required === Infinity) return false;
  return getRoomNumericCapacity(room) >= required;
}

/** User-facing copy when a room cannot be booked/added due to guest vs capacity rules. */
export function getRoomCapacityBlockMessage(room, guestData, roomQuantities, selectedRooms) {
  if (canAddRoomUnit(room, guestData, roomQuantities, selectedRooms)) {
    return null;
  }

  const required = getMinCapacityRequiredForNextUnit(guestData, roomQuantities, selectedRooms);
  const requestedRooms = Math.max(1, Math.floor(Number(guestData?.rooms) || 1));
  const bookedUnits = getBookedRoomUnits(roomQuantities);

  if (required === Infinity || bookedUnits >= requestedRooms) {
    return `You already have ${requestedRooms} room${requestedRooms === 1 ? '' : 's'} selected`;
  }

  const cap = getRoomNumericCapacity(room);
  if (!cap) {
    return 'Not enough space for your guests';
  }

  const children = Math.max(0, Math.floor(Number(guestData?.children) || 0));
  if (children > 0) {
    return `Max allowed guests ${cap}`;
  }
  return `Max allowed adults ${cap}`;
}

export function isRoomSelectionValidForGuests(guestData, roomQuantities, selectedRooms) {
  const totalGuests = getTotalBookingGuests(guestData);
  const requestedRooms = Math.max(1, Math.floor(Number(guestData?.rooms) || 1));
  const units = [];

  for (const room of selectedRooms || []) {
    const qty = Math.max(0, Math.floor(Number(roomQuantities?.[room.id]) || 0));
    const cap = getRoomNumericCapacity(room);
    for (let i = 0; i < qty; i += 1) {
      units.push(cap);
    }
  }

  if (!units.length) return true;
  if (units.length > requestedRooms) return false;
  return canAccommodateGuestsWithUnits(totalGuests, units);
}

export function sortRoomsForGuestFilters(rooms, guestData, roomQuantities, selectedRooms) {
  const list = [...(rooms || [])];
  return list.sort((a, b) => {
    const aOk = canAddRoomUnit(a, guestData, roomQuantities, selectedRooms);
    const bOk = canAddRoomUnit(b, guestData, roomQuantities, selectedRooms);
    if (aOk !== bOk) return aOk ? -1 : 1;

    const aCap = getRoomNumericCapacity(a);
    const bCap = getRoomNumericCapacity(b);
    return aCap - bCap;
  });
}
